import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowDown,
  PlayCircle,
  CheckCircle2,
  FileText,
  ClipboardCheck,
  HandCoins,
  ShieldCheck,
  Calculator,
  BarChart3,
  Users2,
  Boxes,
  Layers,
  Workflow,
  Sparkles,
  Globe2,
  Database,
} from "lucide-react";
import { Nav } from "@/components/landing/Nav";
import { Footer } from "@/components/landing/Footer";
import { SEO } from "@/components/SEO";

/* ───────────────────────────── Data ───────────────────────────── */
const capabilities = [
  { Icon: FileText, title: "Policy Administration", body: "Master policy, COI issuance, lifecycle management." },
  { Icon: ClipboardCheck, title: "Underwriting Engine", body: "Rule-based STP and referral workflows." },
  { Icon: HandCoins, title: "Claims Management", body: "FNOL to settlement with automation." },
  { Icon: ShieldCheck, title: "Reinsurance", body: "Treaty and facultative management." },
  { Icon: Calculator, title: "Accounting", body: "Premium and claims accounting (IFRS-ready)." },
  { Icon: BarChart3, title: "Reporting & Compliance", body: "Regulatory and business reporting." },
];

const useCases = [
  { title: "Group Insurance at Scale", body: "Handle high-volume group policies with efficient onboarding, issuance and servicing." },
  { title: "Retail Under Group (RUG)", body: "Launch and scale byte-sized insurance products across partner ecosystems." },
  { title: "Multi-Line Product Management", body: "Manage life, health, credit and pension products on a single core platform." },
  { title: "Digital Policy Lifecycle", body: "Enable end-to-end digital journeys from issuance to claims." },
];

const platformDepth = [
  "Configurable product engine",
  "Multi-entity and multi-jurisdiction support",
  "API-first integrations",
  "Real-time processing",
  "Workflow automation (STP / NSTP)",
  "Maker–checker governance",
];

const archFlow = [
  { Icon: Globe2, label: "Channels" },
  { Icon: Boxes, label: "Core" },
  { Icon: Workflow, label: "Workflow" },
  { Icon: Database, label: "Data" },
  { Icon: Sparkles, label: "AI" },
];

const outcomes = [
  { value: "60%", label: "Reduction in Operations Cost", sub: "Achieved with leading life insurer" },
  { value: "180M+", label: "Policies Processed", sub: "Annually on the platform" },
  { value: "30%+", label: "Market Share", sub: "In India's Group Life segment" },
];

/* ───────────────────────────── Page ───────────────────────────── */
export default function CorePage() {
  return (
    <main className="bg-white text-ink" data-testid="core-page">
      <SEO
        title="MFInsure® Core — Policy Administration Platform"
        description="MFInsure® Core: configurable, extensible policy administration platform for life, group, credit and pension insurers. Unified policy, underwriting, claims, reinsurance and accounting."
        path="/products/core"
      />
      <Nav />

      {/* HERO */}
      <section data-nav-dark className="relative isolate bg-brand-950 text-white overflow-hidden grain-overlay min-h-[80svh] flex items-center">
        <div aria-hidden className="absolute inset-0 bg-brand-950" />
        <div aria-hidden className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(200,75,49,0.22),transparent_55%)]" />
        <div aria-hidden className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(56,189,248,0.10),transparent_60%)]" />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-36 md:pt-44 pb-24 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7"
          >
            <div className="mb-6">
              <span className="overline text-copper">Gradatim MFInsure® Core</span>
            </div>
            <h1
              data-testid="core-hero-title"
              className="font-display font-medium text-5xl sm:text-6xl md:text-7xl leading-[0.98] tracking-tight"
            >
              The Core Platform for
              <br />
              <span className="text-copper">Modern Insurance</span>
              <span className="text-copper">.</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg md:text-xl text-slate-300/90 leading-relaxed font-light">
              AI-powered, end-to-end platform for Life and General Insurance —
              designed to replace legacy systems and enable scalable, high-performance operations.
            </p>

            <div className="mt-12 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link
                to="/#contact"
                data-testid="core-hero-primary-cta"
                className="group inline-flex items-center justify-center gap-3 bg-copper hover:bg-copper-600 text-white px-8 py-4 text-sm font-medium tracking-[0.12em] uppercase transition-colors"
              >
                Request a demo
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="#capabilities"
                data-testid="core-hero-secondary-cta"
                className="group inline-flex items-center justify-center gap-3 border-2 border-copper text-copper hover:bg-copper hover:text-white px-8 py-4 text-sm font-medium tracking-[0.12em] uppercase transition-colors"
              >
                <PlayCircle className="h-4 w-4" />
                See the core in action
              </a>
            </div>
          </motion.div>

          {/* Right: platform visual */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 hidden lg:block"
          >
            <PlatformVisual />
          </motion.div>
        </div>
      </section>

      {/* WHAT IS MFINSURE CORE */}
      <section className="bg-white">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-20 md:py-24 text-center">
          <span className="overline text-copper">What is MFInsure® Core</span>
          <h2 className="font-display font-medium text-3xl md:text-[44px] tracking-tight leading-[1.1] text-ink mt-5 mb-6">
            A Unified Core for{" "}
            <span className="text-copper">End-to-End Insurance Operations</span>
          </h2>
          <p className="text-base md:text-[18px] text-slate-600 leading-relaxed max-w-[850px] mx-auto">
            MFInsure® Core brings together policy administration, underwriting, claims,
            reinsurance and accounting into a single, modular platform — eliminating
            fragmentation and enabling full operational control.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            {capabilities.slice(0, 5).map((c) => (
              <div key={c.title} className="inline-flex items-center gap-2.5 bg-cream border border-hairline px-4 py-2.5">
                <c.Icon className="h-4 w-4 text-copper" strokeWidth={1.7} />
                <span className="text-sm text-ink">{c.title.replace(" Engine", "")}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CORE CAPABILITIES */}
      <section id="capabilities" style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #F5F7FA 100%)" }} className="border-y border-hairline">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <span className="overline text-copper">Capabilities</span>
            <h2 className="font-display font-medium text-3xl md:text-[44px] tracking-tight leading-[1.1] text-ink mt-5">
              Comprehensive Core{" "}
              <span className="text-copper">Capabilities</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {capabilities.map((c, i) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.06, duration: 0.55 }}
                className="group bg-cream border border-hairline rounded-[14px] p-5 md:p-6 hover:border-copper/40 hover:bg-white transition-colors"
                data-testid={`core-capability-${i}`}
              >
                <div className="h-11 w-11 bg-white border border-hairline group-hover:border-copper flex items-center justify-center mb-5 transition-colors">
                  <c.Icon className="h-5 w-5 text-copper" strokeWidth={1.6} />
                </div>
                <h3 className="font-display text-[18px] md:text-[20px] tracking-tight text-ink mb-2">{c.title}</h3>
                <p className="text-[14px] text-slate-600 leading-relaxed">{c.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="mb-12">
            <span className="overline text-copper">Use Cases</span>
            <h2 className="font-display font-medium text-3xl md:text-[44px] tracking-tight leading-[1.1] text-ink mt-5 max-w-3xl">
              Built for{" "}
              <span className="text-copper">Complex, High-Volume</span> Insurance Models
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {useCases.map((u, i) => (
              <motion.div
                key={u.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.07, duration: 0.55 }}
                className="group bg-cream border border-hairline rounded-2xl p-6 md:p-7 flex items-start gap-5 hover:bg-white hover:border-copper/40 transition-colors"
                data-testid={`core-usecase-${i}`}
              >
                <div className="h-11 w-11 shrink-0 bg-white border border-hairline group-hover:border-copper flex items-center justify-center transition-colors">
                  <Users2 className="h-5 w-5 text-copper" strokeWidth={1.6} />
                </div>
                <div>
                  <h3 className="font-display text-[19px] md:text-[21px] tracking-tight text-ink mb-2">{u.title}</h3>
                  <p className="text-[14px] md:text-[15px] text-slate-600 leading-relaxed">{u.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PLATFORM DEPTH */}
      <section style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #F5F7FA 100%)" }} className="border-y border-hairline">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <span className="overline text-copper">Platform Depth</span>
              <h2 className="font-display font-medium text-3xl md:text-[44px] tracking-tight leading-[1.1] text-ink mt-5">
                Designed for{" "}
                <span className="text-copper">Scale, Flexibility</span> and Control
              </h2>
            </div>
            <div className="lg:col-span-7">
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {platformDepth.map((p, i) => (
                  <motion.li
                    key={p}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-30px" }}
                    transition={{ delay: i * 0.05, duration: 0.4 }}
                    className="flex items-start gap-3 bg-white border border-hairline rounded-xl p-4"
                    data-testid={`core-depth-${i}`}
                  >
                    <CheckCircle2 className="h-5 w-5 text-copper shrink-0 mt-0.5" strokeWidth={1.7} />
                    <span className="text-[14px] md:text-[15px] text-ink leading-snug">{p}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* MINI ARCHITECTURE */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <span className="overline text-copper">Architecture</span>
            <h2 className="font-display font-medium text-3xl md:text-[44px] tracking-tight leading-[1.1] text-ink mt-5">
              Modern, <span className="text-copper">API-First</span> Architecture
            </h2>
            <p className="text-base md:text-[17px] text-slate-600 leading-relaxed mt-5 max-w-[750px] mx-auto">
              Seamlessly integrates with ecosystem partners while ensuring control,
              scalability and real-time operations.
            </p>
          </div>

          {/* Flow */}
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
            {archFlow.map((s, i) => (
              <div key={s.label} className="flex items-center">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  className={`flex items-center gap-3 px-5 py-4 rounded-xl border ${
                    s.label === "AI"
                      ? "bg-ink text-white border-ink"
                      : "bg-cream text-ink border-hairline"
                  }`}
                >
                  <s.Icon className={`h-5 w-5 ${s.label === "AI" ? "text-copper" : "text-copper"}`} strokeWidth={1.6} />
                  <span className="font-display text-[15px] md:text-[17px] tracking-tight">{s.label}</span>
                </motion.div>
                {i < archFlow.length - 1 && (
                  <ArrowRight className="h-4 w-4 text-slate-400 mx-2 md:mx-3 hidden sm:block" />
                )}
                {i < archFlow.length - 1 && (
                  <ArrowDown className="h-4 w-4 text-slate-400 mx-2 sm:hidden" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROOF / OUTCOMES */}
      <section style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #F5F7FA 100%)" }} className="border-y border-hairline">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <span className="overline text-copper">Proof &amp; Outcomes</span>
            <h2 className="font-display font-medium text-3xl md:text-[44px] tracking-tight leading-[1.1] text-ink mt-5">
              Proven in <span className="text-copper">Enterprise Environments</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {outcomes.map((o, i) => (
              <motion.div
                key={o.value}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.08, duration: 0.55 }}
                className="bg-white border border-hairline rounded-2xl p-7 text-center"
                style={{ boxShadow: "0 4px 12px rgba(2,6,23,0.04)" }}
                data-testid={`core-outcome-${i}`}
              >
                <div className="font-display text-5xl md:text-[56px] tracking-tight text-ink leading-none mb-4">
                  {o.value}
                </div>
                <div className="font-display text-[18px] tracking-tight text-copper mb-2 font-medium">
                  {o.label}
                </div>
                <p className="text-[13px] md:text-[14px] text-slate-600 leading-relaxed">{o.sub}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-nav-dark className="relative bg-ink text-white overflow-hidden grain-overlay">
        <div aria-hidden className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-copper/15 blur-3xl" />
        <div aria-hidden className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="relative max-w-[1100px] mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
          <h2 className="font-display font-medium text-3xl md:text-5xl tracking-tight leading-[1.05]">
            Ready to <span className="text-copper">Modernize Your Core?</span>
          </h2>
          <p className="text-base md:text-[18px] text-slate-300 leading-relaxed mt-6 max-w-2xl mx-auto">
            Engage with our team to explore how MFInsure® Core can transform your
            insurance operations.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link
              to="/#contact"
              className="group inline-flex items-center justify-center gap-3 bg-copper hover:bg-copper-600 text-white px-8 py-4 text-sm font-medium tracking-[0.12em] uppercase transition-colors"
              data-testid="core-final-cta-primary"
            >
              Request a demo
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/#contact"
              className="group inline-flex items-center justify-center gap-3 border-2 border-white/30 hover:border-copper hover:text-copper text-white px-8 py-4 text-sm font-medium tracking-[0.12em] uppercase transition-colors"
              data-testid="core-final-cta-secondary"
            >
              Talk to our team
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}

/* ───────────────────────────── Platform visual ───────────────────────────── */
const PlatformVisual = () => (
  <div className="relative aspect-square w-full max-w-[480px] mx-auto">
    <div aria-hidden className="absolute -inset-10 bg-[radial-gradient(circle_at_center,rgba(200,75,49,0.18),transparent_60%)]" />
    <div className="absolute inset-0 border border-white/10" />
    <div className="absolute inset-3 border border-white/5" />
    {/* Dashboard mock */}
    <div className="absolute inset-3 overflow-hidden bg-brand-950 grain-overlay">
      <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full" aria-hidden>
        <defs>
          <linearGradient id="cardGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(255,255,255,0.06)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0.02)" />
          </linearGradient>
        </defs>
        {/* Top bar */}
        <rect x="20" y="20" width="360" height="40" rx="6" fill="url(#cardGrad)" stroke="rgba(255,255,255,0.1)" />
        <circle cx="38" cy="40" r="5" fill="#C84B31" />
        <rect x="54" y="34" width="120" height="6" rx="2" fill="rgba(255,255,255,0.5)" />
        <rect x="54" y="44" width="80" height="3" rx="1.5" fill="rgba(255,255,255,0.25)" />
        {/* KPI cards */}
        {[0, 1, 2].map(i => (
          <g key={i}>
            <rect x={20 + i * 124} y={75} width="116" height="80" rx="6" fill="url(#cardGrad)" stroke="rgba(255,255,255,0.1)" />
            <rect x={32 + i * 124} y={88} width="36" height="3" rx="1.5" fill="rgba(255,255,255,0.35)" />
            <rect x={32 + i * 124} y={102} width="56" height="14" rx="2" fill="#C84B31" opacity="0.85" />
            <rect x={32 + i * 124} y={124} width={50 - i * 8} height="3" rx="1.5" fill="rgba(255,255,255,0.3)" />
            <rect x={32 + i * 124} y={132} width={40 - i * 6} height="3" rx="1.5" fill="rgba(255,255,255,0.2)" />
          </g>
        ))}
        {/* Chart */}
        <rect x="20" y="170" width="240" height="210" rx="6" fill="url(#cardGrad)" stroke="rgba(255,255,255,0.1)" />
        <rect x="32" y="184" width="80" height="4" rx="2" fill="rgba(255,255,255,0.5)" />
        <polyline
          points="32,340 70,300 108,316 146,266 184,290 222,250 250,272"
          fill="none"
          stroke="#C84B31"
          strokeWidth="2.5"
        />
        {[
          [32, 340], [70, 300], [108, 316], [146, 266], [184, 290], [222, 250], [250, 272],
        ].map((p) => (
          <circle key={`pt-${p[0]}-${p[1]}`} cx={p[0]} cy={p[1]} r="3" fill="#C84B31" />
        ))}
        {/* Side list */}
        <rect x="270" y="170" width="110" height="210" rx="6" fill="url(#cardGrad)" stroke="rgba(255,255,255,0.1)" />
        {[0, 1, 2, 3, 4].map(i => (
          <g key={i}>
            <rect x="282" y={186 + i * 38} width="60" height="4" rx="2" fill="rgba(255,255,255,0.5)" />
            <rect x="282" y={196 + i * 38} width="40" height="3" rx="1.5" fill="rgba(255,255,255,0.25)" />
            <circle cx="362" cy={192 + i * 38} r="4" fill="#C84B31" opacity={0.7 - i * 0.1} />
          </g>
        ))}
      </svg>
    </div>
  </div>
);
